import { isEmpty } from 'lodash-es'

import { MLM, MsTracking, NOT_SET, TrackingParameter, TrackingType, addTracking } from '@sunrise/tracking'

import { DecoratedEstate } from '../../../ts/mapper/decoratedItemEstate.interface'
import { useResultListStore } from '../../../ts/store/resultListStoreModule/resultListStoreModule'
import { ActionIds, MLMData } from '../../../ts/tracking/mlmActionIds.enum'
import { staticMlmTrackingData } from '../../../ts/tracking/mlmStaticTrackingData'
import { findMinMaxPriceInResults } from '../../../ts/utils/searchListUtils'
import { buildMlmResultListItems } from '../../../ts/utils/trackingUtils'

import { tealiumTrackingParameter } from './M_RealEstate_SearchList.enum'

export function buildSearchBoxTracking(
    used: tealiumTrackingParameter,
    totalCount: number,
    initialTotalCount: number,
    items: DecoratedEstate[],
): TrackingParameter {
    const resultListStore = useResultListStore()
    return {
        mslayer_internal_searchbox_filter_element_rooms_min: determineValueForParameter(
            resultListStore.searchParams.roomi,
        ),
        mslayer_internal_searchbox_filter_element_rooms_max: determineValueForParameter(
            resultListStore.searchParams.rooma,
        ),
        mslayer_internal_searchbox_filter_element_type:
            resultListStore.searchParams.esr?.toString() + '_' + resultListStore.searchParams.etype?.toString() ||
            NOT_SET,
        mslayer_internal_searchphrases_city_combo: resultListStore.searchParams.location || NOT_SET,
        mslayer_internal_searchbox_filter_element_price_min: determineValueForParameter(
            resultListStore.searchParams.primi,
        ),
        mslayer_internal_searchbox_filter_element_price_max: determineValueForParameter(
            resultListStore.searchParams.prima,
        ),
        mslayer_internal_searchbox_resultlist_price_min: findMinMaxPriceInResults(
            items,
            totalCount,
        ).minPrice.toString(),
        mslayer_internal_searchbox_resultlist_price_max: findMinMaxPriceInResults(
            items,
            totalCount,
        ).maxPrice.toString(),
        mslayer_internal_searchbox_filter_element_space_min: determineValueForParameter(
            resultListStore.searchParams.flmi,
        ),
        mslayer_internal_searchbox_filter_element_space_max: determineValueForParameter(
            resultListStore.searchParams.flma,
        ),
        mslayer_internal_searchbox_filter_element_radius: determineValueForParameter(resultListStore.searchParams.sr),
        mslayer_internal_searchbox_filter_element_equipment: isEmpty(resultListStore.searchParams.eqid)
            ? NOT_SET
            : resultListStore.searchParams.eqid || NOT_SET,
        mslayer_internal_searchbox_filter_element_furniture: determineValueForParameter(
            resultListStore.searchParams.furnid,
        ),
        mslayer_internal_searchbox_filter_element_categories: isEmpty(resultListStore.searchParams.ecat)
            ? NOT_SET
            : resultListStore.searchParams.ecat || NOT_SET,
        mslayer_internal_searchbox_used: used,
        mslayer_page_number: determineValueForParameter(resultListStore.searchParams.page),
        mslayer_list_results_max: initialTotalCount?.toString(),
        mslayer_list_results_after_filter: totalCount?.toString(),
        mslayer_list_sorting_logic: determineValueForParameter(resultListStore.searchParams.sort),
    }
}

export function createMlmViewTracking(
    componentId: string,
    pageUrl: string,
    items: DecoratedEstate[],
): Record<string, string> {
    const trackingData: MLM.TrackingData = buildMlmTrackingData(componentId, pageUrl, items, MLMData.DEFAULT_PAGE)
    const mlmTracking: MsTracking = MLM.createMlmTracking(trackingData, TrackingType.PAGE_VIEW)

    return addTracking([mlmTracking])
}

export function trackMlm(componentId: string, pageUrl: string, items: DecoratedEstate[], page: number): void {
    const mlmTrackingData: MLM.TrackingData = buildMlmTrackingData(componentId, pageUrl, items, page)

    MLM.trackClick(mlmTrackingData)
}

function buildMlmTrackingData(
    componentId: string,
    pageUrl: string,
    items: DecoratedEstate[],
    page: number,
): MLM.TrackingData {
    return {
        ...staticMlmTrackingData,
        page: page,
        actionId: ActionIds.PAGE_VIEW,
        componentId,
        pageUrl,
        items: buildMlmResultListItems(items),
    }
}

function determineValueForParameter(value: number | string | undefined) {
    return value === 0 ? NOT_SET : value?.toString() || NOT_SET
}
